import {
  Modal,
  ActionPopUp,
  ConfirmationModal,
  ControlledDropDown,
  CustomButton,
  MultiLineInput,
} from '@ui-kit'
import { useEffect, useState } from 'react'
import { STRINGS } from '../../utils/strings'
import {
  editEntityData,
  editEntityDataEmbeddedAPI,
  getEntityData,
  getEntityDataByIdEmbedded,
  getEntityDataByIdEntityNameEmbedded,
} from '../../apis/entityData.api'
import { EntityLayout, LayoutData } from '../../types/layout.types'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  addToaster,
  DropdownType,
  getLayoutByIdApi,
  hideLoader,
  LayoutResponseType,
  showLoader,
} from '@app-store'
import {
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
  PERMISSION_ACCESSIBILITY,
  PERMISSION_MODULES,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import FormRenderer from '../../components/FormRenderer'
import { isEmpty, noop, uniqueId } from 'lodash'
import { FieldValues, useForm } from 'react-hook-form'
import {
  fetchTaskForAssignUnassign,
  caseCancelOrVoidApi,
  fetchUsersForCase,
} from '../../apis/caseStatus.api'
import { CaseActionList, CaseStateType } from '../../types/reassignCase.types'
import { v4 as uuid } from 'uuid'
import { getMigrationStatus } from '../../utils/migration.utils'
import { RECORD_STATUS } from '../../constants/form.constants'
import { permissionObject } from '../../utils/helperFunctions'
import { currentUserId, isSuperAdmin } from '../../utils/permission'
import { ProgramPermissionsDataType } from '../../types/permission.types'

const CaseStateWorkflow = ({
  caseId,
  workflowInstanceId,
  refetchWorkflow,
  voidData,
  closeData,
  entityId,
  overallCaseStatus,
  faxDetails,
  onCaseDataUpdate,
}: CaseStateType) => {
  const [caseCurrentState, setCaseCurrentState] = useState<string>('')
  const [formModal, setFormModal] = useState<boolean>(false)
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false)
  const [viewLayout, setViewLayout] = useState<LayoutResponseType>()
  const [reassignUserId, setReassignUserId] = useState<string>()
  const [reasonData, setReasonData] = useState<FieldValues | null>(null)
  const [actionList, setActionList] = useState<CaseActionList[]>([])
  const [reassignOptions, setReassignOptions] = useState<
    DropdownType<string>[]
  >([])
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const [isCaseCompletionDate, setIsCaseCompletionDate] =
    useState<boolean>(false)
  const relationshipTree = useAppSelector(
    (state) => state?.workflowData?.relationshipTree
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const workflowInstanceAdditionalKeys = useAppSelector(
    (state) => state?.workflowData?.workflowInstanceAdditionalKeys
  )
  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const [isWorkflowInProgress, setIsWorkflowInProgress] =
    useState<boolean>(false)

  const authUser = useAppSelector((state) => state?.authUser)
  const authUserId = authUser?.user?.id
  const clientPermissions = useAppSelector((state) => state?.clientPermissions)
  const caseData = useAppSelector((state) => state?.workflowData?.caseData)
  const program_id = caseData?.programs?.id?.toString() ?? ''
  const case_type = caseData?.services?.service_name ?? ''
  const assignedUserId = caseData?.users?.id
  const isCaseOwner = assignedUserId === authUserId
  const isCaseUnassigned = !assignedUserId
  const [entityData, setEntityData] = useState<FieldValues>()
  const dispatch = useAppDispatch()
  const [notes, setNotes] = useState('')
  const allPermissions = useAppSelector(
    (state) => state?.allPermissions?.permissions
  )

  const getPermissionIdFromCaseType = (caseType: string): string => {
    const permission = allPermissions.find(
      (p) =>
        p.services?.some((s) => s.service_name === caseType) ||
        p.name === caseType
    )
    return permission?.id?.toString() ?? ''
  }
  const fetchDataForReassignCase = async () => {
    try {
      dispatch(showLoader())
      setFormModal(true)
      const permissionId = getPermissionIdFromCaseType(case_type)
      const data = await fetchUsersForCase(program_id?.toString(), permissionId)
      if (!data?.data?.length) {
        handleCloseModal()
        dispatch(
          addToaster({
            message: STRINGS.NO_USER_FOUND,
            type: 'info',
            id: uniqueId(),
          })
        )
        return
      }
      let options: { label: string; value: string }[] = []
      if (
        isSuperAdmin() ||
        isCaseOwner ||
        permissionObject.Queue_Bulk_Assignment_Permission(
          programPermissions,
          PERMISSION_MODULES.BULK_WORK_ASSIGNMENTS,
          PERMISSION_ACCESSIBILITY.MANAGE,
          program_id
        ) ||
        (isCaseUnassigned &&
          permissionObject.Queue_Case_Assignment(
            programPermissions,
            program_id,
            case_type
          ))
      ) {
        options = data?.data?.map(
          (user: { id: number; first_name: string; last_name: string }) => ({
            label: `${user.first_name} ${user.last_name}`.trim(),
            value: user.id.toString(),
          })
        )
      }
      setReassignOptions(options)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const checkReopenWorkPermission = async () => {
    try {
      const caseDataResponse = await getEntityDataByIdEntityNameEmbedded(
        'case',
        ['programs', 'services'],
        caseId || ''
      )
      if (caseDataResponse.data) {
        const servicePermissionValue = permissionObject.Queue_Review_Manage(
          programPermissions,
          caseDataResponse.data.programs?.id?.toString() ?? '',
          caseDataResponse.data.services?.service_name?.toString() ?? ''
        )
        if (servicePermissionValue) {
          const program_id =
            caseDataResponse.data.programs?.id?.toString() ?? ''
          const reopenPermissionValue =
            programPermissions[program_id]?.['Reopen Work']?.Manage
          return reopenPermissionValue
        } else {
          return false
        }
      }
    } catch (err) {
      return false
      console.error(err)
    }
  }
  const fetchAssigneeData = async () => {
    try {
      const userEntityId =
        workflowDefinition?.global_variables?.entities?.users?.entity_id

      const userEntity = relationshipTree?.find(
        (item) => item?.entity_id?.toString() === userEntityId?.toString()
      )
      if (!isEmpty(userEntity)) {
        const response = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowDefinition?.global_variables?.entity_id),
          [userEntity?.field_name ?? ''],
          Number(caseId)
        )
        const hasReopenPermissionValue = await checkReopenWorkPermission()
        if (isSuperAdmin()) {
          overAllCaseStatuses(response?.data, true)
        } else {
          overAllCaseStatuses(response?.data, hasReopenPermissionValue)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const displayMessage = () => {
    if (isCaseCompletionDate) {
      return STRINGS.CASE_COMPLETION_OVERRIDE_MESSAGE
    }
    switch (caseCurrentState) {
      case STRINGS.UNASSIGN_CASE:
        return STRINGS.UNASSIGN_CASE_MESSAGE
      case STRINGS.CLOSE_CASE:
        return STRINGS.CLOSE_CASE_MESSAGE
      case STRINGS.VOID_CASE:
        return STRINGS.VOID_CASE_MESSAGE
      default:
        return STRINGS.INVALID_MESSAGE
    }
  }
  const changeCaseState = (key: string) => {
    if (key !== STRINGS.REOPEN_CASE) {
      setCaseCurrentState(key)
    }
    switch (key) {
      case STRINGS.REASSIGN_CASE:
        fetchDataForReassignCase()
        return
      case STRINGS.UNASSIGN_CASE:
        setConfirmationModal(true)
        return
      case STRINGS.VOID_CASE:
        fetchLayout(voidData?.entity_id ?? '', voidData?.layout_id ?? '')
        return
      case STRINGS.CLOSE_CASE:
        fetchLayout(closeData?.entity_id ?? '', closeData?.layout_id ?? '', key)
        return
      case STRINGS.REOPEN_CASE:
        reOpenCaseApi()
        return

      default:
        return null
    }
  }
  const fetchLayoutData = async (
    layout: LayoutResponseType,
    caseSummaryId?: string
  ) => {
    try {
      const response = await getEntityData(
        layout?.entity_name,
        Number(layout?.entity_id),
        Number(layout?.id),
        caseSummaryId ?? 0
      )
      if (response?.data) {
        setEntityData(response.data)
      }
    } catch (err) {
      handleCloseModal()
      console.error(err)
    }
  }
  const fetchLayout = async (
    entityId: string,
    layoutId: string,
    _type?: string
  ) => {
    dispatch(showLoader())
    try {
      const caseSummaryId = await getEntityDataByIdEmbedded(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(workflowDefinition?.global_variables?.entity_id),
        ['case_summary'],
        caseId
      )
      const response = await getLayoutByIdApi({
        entityId: entityId,
        layoutId: layoutId,
      })
      if (response?.data) {
        setFormModal(true)
        setViewLayout(response.data)
        if (response?.data?.type === LAYOUT_TYPE_NAMESPACE.DEFAULT_VIEW_EDIT) {
          await fetchLayoutData(
            response.data,
            caseSummaryId?.data?.case_summary?.id
          )
          setNotes('')
        }
      }
    } catch (err) {
      console.error(err)
      handleCloseModal()
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleCloseModal = () => {
    if (isCaseCompletionDate) {
      setIsCaseCompletionDate(false)
    }
    setReassignUserId('')
    setValue('agent_list', null)
    setReasonData(null)
    setCaseCurrentState('')
    setFormModal(false)
    setConfirmationModal(false)
  }
  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      agent_list: null,
    },
  })
  const fetchTaskCorrespondingData = async () => {
    try {
      const taskEntityId =
        workflowDefinition?.global_variables?.entities?.task?.entity_id ?? '62'
      const programEntity = relationshipTree?.find(
        (item) => item?.entity_id?.toString() === taskEntityId
      )
      return programEntity
    } catch (error) {
      console.error(error)
    }
  }
  const fetchNotesCorrespondingData = async () => {
    try {
      const notesEntityId =
        workflowDefinition?.global_variables?.entities?.case_notes?.entity_id ??
        '533'
      const notesEntity = relationshipTree?.find(
        (item) => item?.entity_id?.toString() === notesEntityId
      )
      return notesEntity
    } catch (error) {
      console.error(error)
    }
  }
  const onSubmit = async () => {
    try {
      dispatch(showLoader())
      const data = await fetchTaskForAssignUnassign(
        caseId?.toString(),
        workflowInstanceId?.toString()
      )

      const newData = await fetchTaskCorrespondingData()
      const dataForEmbedded = {
        id: caseId,
        user_id:
          caseCurrentState === STRINGS.REASSIGN_CASE ? reassignUserId : null,
        ...(data?.data?.length
          ? {
              [`${newData?.field_name?.toString()}`]: data?.data?.map(
                (val: FieldValues) => {
                  return {
                    id: val?.id,
                    assignee: {
                      id:
                        caseCurrentState === STRINGS.REASSIGN_CASE
                          ? Number(reassignUserId)
                          : 0,
                      _state: NAMESPACES?.CHANGED,
                    },
                    _state: NAMESPACES?.CHANGED,
                  }
                }
              ),
            }
          : {}),
      }
      await editEntityDataEmbeddedAPI(
        entityId ?? '',
        caseId ?? '',
        dataForEmbedded,
        [
          ...(data?.data?.length ? [`${newData?.field_name?.toString()}`] : []),
          'user_id',
        ],
        true
      )
      dispatch(
        addToaster({
          id: uniqueId(),
          message:
            caseCurrentState === STRINGS.REASSIGN_CASE
              ? STRINGS.REASSIGN_TOASTER_MSG
              : STRINGS.UNASSIGN_TOASTER_MSG,
          type: 'success',
        })
      )
      onCaseDataUpdate?.()
    } catch (error) {
      dispatch(hideLoader())
      dispatch(
        addToaster({
          message: STRINGS.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      console.error(error)
    } finally {
      handleCloseModal()
      dispatch(hideLoader())
      refetchWorkflow()
    }
  }
  const reOpenCaseApi = async () => {
    try {
      dispatch(showLoader())
      const migrated = await getMigrationStatus(Number(caseId), true)
      if (migrated) {
        dispatch(
          addToaster({
            id: uuid(),
            message: STRINGS?.MIGRATED_CASE_ERROR_MESSAGE,
            type: 'error',
          })
        )
      } else {
        await caseCancelOrVoidApi(
          workflowInstanceId?.toString(),
          STRINGS.REOPEN_STRING,
          caseData?.programs?.id?.toString(),
          caseData?.services?.id?.toString()
        )
        dispatch(
          addToaster({
            id: uniqueId(),
            message: STRINGS.REOPEN_TOASTER_MSG,
            type: 'success',
          })
        )
      }
    } catch (error) {
      dispatch(
        addToaster({
          message: STRINGS.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      console.error(error)
    } finally {
      dispatch(hideLoader())
      refetchWorkflow()
    }
  }
  const setCaseCompletionDateApi = async () => {
    try {
      if (isCaseCompletionDate) {
        dispatch(showLoader())
      }
      await editEntityDataEmbeddedAPI(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(caseId),
        { case_completion_date: new Date().toISOString() },
        ['case_completion_date']
      )
      if (isCaseCompletionDate) {
        setIsCaseCompletionDate(false)
        dispatch(hideLoader())
        refetchWorkflow()
      }
    } catch (error) {
      console.error(error)
    }
  }
  const getEditEntityParams = (type: string) => {
    const isClose = type === STRINGS.CLOSE_CASE
    const name = isClose
      ? closeData?.entity_name ?? ''
      : voidData?.entity_name ?? ''
    const id = isClose
      ? Number(workflowDefinition?.global_variables?.entity_id)
      : Number(voidData?.entity_id)
    const layoutId = isClose
      ? Number(workflowDefinition?.global_variables?.left_split?.edit_layout_id)
      : Number(voidData?.layout_id)
    const data = isClose ? { case_summary: reasonData } : reasonData
    const targetId = caseId

    return { name, id, layoutId, data, targetId }
  }

  const getEditEntityParamsFromEntity = (type: string) => {
    const isClose = type === STRINGS.CLOSE_CASE
    const name = isClose
      ? closeData?.entity_name ?? ''
      : voidData?.entity_name ?? ''
    const id = isClose
      ? Number(closeData?.entity_id)
      : Number(voidData?.entity_id)
    const layoutId = isClose
      ? Number(closeData?.layout_id)
      : Number(voidData?.layout_id)
    const data = reasonData
    const targetId = entityData?.id
    return { name, id, layoutId, data, targetId }
  }

  const voidCloseCase = async (type = STRINGS.CLOSE_CASE) => {
    try {
      dispatch(showLoader())
      if (reasonData) {
        const { name, id, layoutId, data, targetId } = isEmpty(entityData)
          ? getEditEntityParams(type)
          : getEditEntityParamsFromEntity(type)

        await editEntityData(name, id, layoutId, data as FieldValues, targetId)
        if (notes.trim().length > 0) {
          const newData = await fetchNotesCorrespondingData()
          const dataForEmbedded = {
            [`${newData?.field_name?.toString()}`]: [
              {
                name: notes.trim(),
                source:
                  type === STRINGS.CLOSE_CASE
                    ? NAMESPACES.CLOSURE
                    : NAMESPACES.VOID,
                headers:
                  type === STRINGS.CLOSE_CASE
                    ? NAMESPACES.CASE_CLOSURE_NOTES
                    : NAMESPACES.CASE_VOID_NOTES,
                _state: NAMESPACES.ADDED,
                users_id: currentUserId() ?? '',
              },
            ],
            record_status: RECORD_STATUS.ACTIVE,
          }
          await editEntityDataEmbeddedAPI(
            workflowDefinition?.global_variables?.entity_id ?? '',
            caseId,
            dataForEmbedded,
            [newData?.field_name?.toString() ?? ''],
            true
          )
          setNotes('')
        }
      }

      const isClose = type === STRINGS.CLOSE_CASE

      await caseCancelOrVoidApi(
        workflowInstanceId?.toString(),
        isClose ? STRINGS.SUSPEND : STRINGS.CANCEL
      )

      const response = await getEntityDataByIdEmbedded(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(workflowDefinition?.global_variables?.entity_id),
        ['case_completion_date'],
        Number(caseId)
      )

      dispatch(
        addToaster({
          id: uniqueId(),
          message: isClose
            ? STRINGS.CLOSE_TOASTER_MSG
            : STRINGS.VOID_TOASTER_MSG,
          type: 'success',
        })
      )

      if (!response?.data?.case_completion_date) {
        await setCaseCompletionDateApi()
      } else {
        setIsCaseCompletionDate(true)
      }
    } catch (error) {
      console.error(error)
      dispatch(
        addToaster({
          message: STRINGS.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
    } finally {
      handleCloseModal()
      dispatch(hideLoader())
      refetchWorkflow()
    }
  }

  const saveEntityData = async (data: FieldValues) => {
    setReasonData(data)

    handleSaveCloseVoid()
  }

  const handleSaveCloseVoid = () => {
    setFormModal(false)
    setConfirmationModal(true)
  }
  const handleYesConfirmModal = () => {
    if (isCaseCompletionDate) {
      setCaseCompletionDateApi()
      return
    }
    switch (caseCurrentState) {
      case STRINGS.UNASSIGN_CASE:
        onSubmit()
        return
      case STRINGS.VOID_CASE:
        voidCloseCase(STRINGS.VOID_CASE)
        return
      case STRINGS.CLOSE_CASE:
        voidCloseCase()
        return
      default:
        return null
    }
  }
  const caseCompletionCloseModal = () => {
    if (isCaseCompletionDate) {
      setIsCaseCompletionDate(false)
    } else handleCloseModal()
  }
  const canUserReassignCase = (
    programPermissions: ProgramPermissionsDataType,
    program_id: string,
    case_type: string,
    isCaseOwner: boolean,
    isCaseUnassigned: boolean
  ) => {
    if (isSuperAdmin()) return true
    if (
      permissionObject.Queue_Bulk_Assignment_Permission(
        programPermissions,
        PERMISSION_MODULES.BULK_WORK_ASSIGNMENTS,
        PERMISSION_ACCESSIBILITY.MANAGE,
        program_id
      )
    )
      return true
    if (isCaseOwner) {
      return permissionObject.Queue_Case_Reassignment(
        programPermissions,
        program_id,
        case_type
      )
    }
    if (isCaseUnassigned) {
      return permissionObject.Queue_Case_Assignment(
        programPermissions,
        program_id,
        case_type
      )
    }
    return false
  }
  const canUserUnassignCase = (
    programPermissions: ProgramPermissionsDataType,
    program_id: string,
    case_type: string,
    isCaseOwner: boolean
  ) => {
    if (isSuperAdmin()) return true
    if (
      permissionObject.Queue_Bulk_Assignment_Permission(
        programPermissions,
        PERMISSION_MODULES.BULK_WORK_ASSIGNMENTS,
        PERMISSION_ACCESSIBILITY.MANAGE,
        program_id
      )
    )
      return true
    if (isCaseOwner) {
      return permissionObject.Queue_Case_Reassignment(
        programPermissions,
        program_id,
        case_type
      )
    }
    return false
  }

  const overAllCaseStatuses = (
    users: FieldValues,
    hasReopenPermission: boolean | undefined
  ) => {
    let statusData = [
      {
        label: STRINGS.REASSIGN_CASE,
        key: STRINGS.REASSIGN_CASE,
      },
      { label: STRINGS.UNASSIGN_CASE, key: STRINGS.UNASSIGN_CASE },
      { label: STRINGS.CLOSE_CASE, key: STRINGS.CLOSE_CASE },
      ...(hasReopenPermission
        ? [{ label: STRINGS.REOPEN_CASE, key: STRINGS.REOPEN_CASE }]
        : []),
      { label: STRINGS.VOID_CASE, key: STRINGS.VOID_CASE },
    ]
    if (!users?.users) {
      statusData = statusData?.filter(
        (val) => val?.key !== STRINGS.UNASSIGN_CASE
      )
    }
    if (
      !canUserReassignCase(
        programPermissions,
        program_id,
        case_type,
        isCaseOwner,
        isCaseUnassigned
      )
    ) {
      statusData = statusData?.filter(
        (val) => val.key !== STRINGS.REASSIGN_CASE
      )
    }
    if (
      !canUserUnassignCase(
        programPermissions,
        program_id,
        case_type,
        isCaseOwner
      )
    ) {
      statusData = statusData.filter((val) => val.key !== STRINGS.UNASSIGN_CASE)
    }
    switch (overallCaseStatus) {
      case STRINGS.CLOSED:
        statusData = statusData?.filter(
          (val) => val?.key !== STRINGS.CLOSE_CASE
        )
        break
      case STRINGS.OPEN:
      case STRINGS.MISSING_INFORMATION:
        statusData = statusData?.filter(
          (val) => val?.key !== STRINGS.REOPEN_CASE
        )
        break
      case STRINGS.VOID:
        statusData = []
        break
      default:
        break
    }
    setActionList(statusData)
  }

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e?.target?.value)
  }

  useEffect(() => {
    if (
      relationshipTree &&
      workflowDefinition &&
      overallCaseStatus?.length &&
      caseData
    )
      fetchAssigneeData()
  }, [
    relationshipTree,
    workflowDefinition,
    overallCaseStatus,
    clientPermissions,
    caseData,
  ])

  useEffect(() => {
    if (!isEmpty(workflowInstance)) {
      if (
        workflowInstance?.state === WORKFLOW_STATUS.PROCESSING_IN_BACKGROUND ||
        workflowInstanceAdditionalKeys?.background_jobs_active ||
        workflowInstanceAdditionalKeys?.isPolling
      ) {
        setIsWorkflowInProgress(true)
      } else {
        setIsWorkflowInProgress(false)
      }
    }
  }, [workflowInstance, workflowInstanceAdditionalKeys])

  return (
    <>
      {caseCurrentState?.length || isCaseCompletionDate ? (
        <Modal
          isOpen={true}
          closeModal={handleCloseModal}
          showCloseButton={true}
          width={`${
            caseCurrentState === STRINGS.REASSIGN_CASE
              ? 'w-[30rem]'
              : 'w-[40rem]'
          }`}
        >
          {formModal
            ? (() => {
                switch (caseCurrentState) {
                  case STRINGS.REASSIGN_CASE:
                    return (
                      <div className="flex flex-col w-full">
                        <div className="bg-tertiary-100 w-full p-3">
                          <div className="pl-2 font-medium">
                            {STRINGS.REASSIGN_CASE}
                          </div>
                        </div>

                        <div className="p-3">
                          <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-4"
                          >
                            <ControlledDropDown
                              name="agent_list"
                              control={control}
                              label={STRINGS.SELECT_AGENT}
                              isSearchable={true}
                              options={reassignOptions}
                              required={true}
                              onChange={(e) => setReassignUserId(e?.value)}
                            />

                            <div className="flex justify-end space-x-3 pt-4">
                              <CustomButton
                                onClick={() => {
                                  handleCloseModal()
                                }}
                              >
                                <span>{NAMESPACES.CANCEL_BUTTON}</span>
                              </CustomButton>

                              <CustomButton
                                isFilled
                                type="submit"
                                disabled={!reassignUserId}
                              >
                                <span>{NAMESPACES.REASSIGN_BUTTON}</span>
                              </CustomButton>
                            </div>
                          </form>
                        </div>
                      </div>
                    )

                  case STRINGS.CLOSE_CASE:
                  case STRINGS.VOID_CASE:
                    return entityData ? (
                      <div className="mt-3 border border-tertiary-300 rounded overflow-y-auto ">
                        <div className="bg-tertiary-100 w-full p-3">
                          <div className="pl-2 font-medium">
                            {caseCurrentState === STRINGS.CLOSE_CASE
                              ? STRINGS.CLOSE_CASE
                              : STRINGS.VOID_CASE}
                          </div>
                        </div>
                        <div className="px-3 pb-3 pt-2">
                          <FormRenderer
                            heading={''}
                            layout={viewLayout?.layout_data as LayoutData}
                            mode={'edit'} //todo add or edit based on data to save where case or else
                            handleFormSubmit={saveEntityData}
                            fullLayout={viewLayout as EntityLayout}
                            data={entityData}
                            footer={
                              <div>
                                <div className="mt-3">
                                  <MultiLineInput
                                    name=""
                                    label="Notes"
                                    value={notes}
                                    onChange={handleNotesChange}
                                  />
                                </div>
                                <div className="flex justify-end space-x-3 p-4">
                                  <CustomButton onClick={handleCloseModal}>
                                    <span>{NAMESPACES.CANCEL_BUTTON}</span>
                                  </CustomButton>
                                  <CustomButton isFilled type="submit">
                                    <span>{NAMESPACES.SUBMIT}</span>
                                  </CustomButton>
                                </div>
                              </div>
                            }
                          />
                        </div>
                      </div>
                    ) : null
                  default:
                    return null
                }
              })()
            : null}

          {confirmationModal || isCaseCompletionDate ? (
            <ConfirmationModal
              message={displayMessage()}
              handleNo={caseCompletionCloseModal}
              handleYes={handleYesConfirmModal}
            ></ConfirmationModal>
          ) : null}
        </Modal>
      ) : null}
      {isEmpty(faxDetails) && (
        <ActionPopUp
          actions={actionList}
          row={[]}
          rowIndex={999}
          onActionClick={changeCaseState}
          setRowPosition={noop}
          isHovered={true}
          isDisabled={
            isWorkflowInProgress || overallCaseStatus === STRINGS.VOID
          }
        />
      )}
    </>
  )
}

export default CaseStateWorkflow
